
import { supabase } from "@/integrations/supabase/client";
import { Chat, Message } from "../types";

export const fetchChatMessagesFromSupabase = async (chatId: string) => {
  try {
    const { data: messagesData, error: messagesError } = await supabase
      .from('messages')
      .select('*')
      .eq('chat_id', chatId)
      .order('timestamp', { ascending: true });

    if (messagesError) {
      console.log(`Error fetching messages for chat ${chatId}:`, messagesError);
      return [];
    }

    if (!messagesData) {
      console.log(`No messages found for chat ${chatId}`);
      return [];
    }

    return messagesData.map(msg => {
      const typedSender = msg.sender === "user" || msg.sender === "team" 
        ? msg.sender as "user" | "team"
        : "user"; // Default to "user" if unexpected value
        
      return {
        id: msg.id,
        text: msg.text,
        sender: typedSender,
        timestamp: msg.timestamp ? new Date(msg.timestamp).getTime() : Date.now(),
        isRead: msg.is_read === true
      };
    }) as Message[];
  } catch (error) {
    console.log(`Unexpected error fetching messages for chat ${chatId}:`, error);
    return [];
  }
};

export const fetchChatsFromSupabase = async () => {
  try {
    const { data: chatData, error: chatError } = await supabase
      .from('chats')
      .select('*')
      .order('last_activity', { ascending: false });

    if (chatError) {
      console.error('Error fetching chats:', chatError);
      return [];
    }

    if (!chatData || chatData.length === 0) {
      console.log('No chats found in Supabase');
      return [];
    }

    console.log(`Successfully fetched ${chatData.length} chats from Supabase`);
    return chatData;
  } catch (error) {
    console.error('Unexpected error fetching chats:', error);
    return [];
  }
};

export const updateChatInSupabase = async (chatId: string, updateData: Partial<Chat>) => {
  try {
    console.log(`Updating chat ${chatId} with data:`, updateData);
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const supabaseData: any = {};
    
    if (updateData.assignedTo !== undefined) {
      supabaseData.assigned_to = updateData.assignedTo;
    }
    
    if (updateData.isActive !== undefined) {
      supabaseData.is_active = updateData.isActive;
    }
    
    if (updateData.status !== undefined) {
      supabaseData.status = updateData.status;
    }
    
    if (updateData.lastActivity !== undefined) {
      supabaseData.last_activity = new Date(updateData.lastActivity).toISOString();
    }

    const { error } = await supabase
      .from('chats')
      .update(supabaseData)
      .eq('id', chatId);

    if (error) {
      console.log('Error updating chat:', error);
      return false;
    }
    
    console.log(`Successfully updated chat ${chatId}`);
    return true;
  } catch (error) {
    console.log(`Unexpected error updating chat ${chatId}:`, error);
    return false;
  }
};

export const sendMessageToSupabase = async (chatId: string, message: Omit<Message, 'id'>) => {
  try {
    console.log(`Sending message to chat ${chatId}:`, message.text);
    
    if (message.sender !== "user" && message.sender !== "team") {
      console.error(`Invalid sender type: ${message.sender}`);
      return false;
    }
    
    const { error: updateError } = await supabase
      .from('chats')
      .update({ 
        last_activity: new Date().toISOString(),
        status: message.sender === "team" ? 'active' : 'waiting' // Set status based on sender
      })
      .eq('id', chatId);
      
    if (updateError) {
      console.log('Error updating chat activity timestamp:', updateError);
    }
    
    const { data, error } = await supabase
      .from('messages')
      .insert({
        chat_id: chatId,
        text: message.text,
        sender: message.sender,
        timestamp: new Date().toISOString(),
        is_read: message.isRead
      })
      .select();

    if (error) {
      console.error('Error sending message:', error);
      return false;
    }
    
    console.log(`Successfully sent message to chat ${chatId}`, data);
    return true;
  } catch (error) {
    console.error(`Unexpected error sending message to chat ${chatId}:`, error);
    return false;
  }
};

/**
 * Enables realtime updates for a specific chat
 */
export const enableRealtimeForChat = async (chatId: string) => {
  try {
    console.log(`Enabling realtime updates for chat ${chatId}`);
    
    // Set up a dedicated channel for this specific chat
    const channel = supabase
      .channel(`chat-${chatId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'messages',
        filter: `chat_id=eq.${chatId}`
      }, (payload) => {
        console.log(`Realtime update for chat ${chatId}:`, payload);
      })
      .subscribe((status) => {
        console.log(`Realtime subscription status for chat ${chatId}: ${status}`);
      });
      
    return true;
  } catch (error) {
    console.error('Error enabling realtime:', error);
    return false;
  }
};
